import { useState, useEffect } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import { formatDate } from "../utils/formatDate";

function Cycles({ user, db, setSelectedCycle, selectedCycle, setTab }) {
  const [cycles, setCycles] = useState([]);
  const [cycleName, setCycleName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCycles = async () => {
      if (!user) {
        setCycles([]);
        return;
      }
      setLoading(true);
      try {
        const cycleSnapshot = await getDocs(collection(db, `users/${user.uid}/cycles`));
        const cycleData = cycleSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCycles(cycleData);
        setError("");
      } catch (err) {
        setError("Error fetching cycles. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchCycles();
  }, [user, db]);

  const addCycle = async (e) => {
    e.preventDefault();
    if (!user) {
      return; // Modal is handled by JournalApp
    }

    if (!cycleName.trim() || !startDate || !endDate) {
      setError("Please fill in all fields.");
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      setError("End date cannot be before start date.");
      return;
    }
    setLoading(true);
    try {
      const newCycle = {
        name: cycleName,
        startDate: startDate,
        endDate: endDate,
        createdAt: new Date(),
      };
      const docRef = await addDoc(collection(db, `users/${user.uid}/cycles`), newCycle);
      setCycles([...cycles, { id: docRef.id, ...newCycle }]);
      setCycleName("");
      setStartDate("");
      setEndDate("");
      setError("");
      setSelectedCycle({ id: docRef.id, ...newCycle });
      setTab("journal");
    } catch (err) {
      setError("Error adding cycle. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteCycle = async (id) => {
    if (!user) {
      return; // Modal is handled by JournalApp
    }
    setLoading(true);
    try {
      await deleteDoc(doc(db, `users/${user.uid}/cycles`, id));
      setCycles(cycles.filter((cycle) => cycle.id !== id));
      if (selectedCycle?.id === id) {
        setSelectedCycle(null);
      }
      setError("");
    } catch (err) {
      setError("Error deleting cycle. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card animate-fade-in w-full mt-0">
      <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">
        Your 66-Day Cycles
      </h2>
      {error && (
        <p className="text-error mb-4">{error}</p>
      )}
      {loading && (
        <div className="flex justify-center items-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" aria-label="Loading cycles" />
        </div>
      )}
      <form onSubmit={addCycle} className="mb-6 space-y-4">
        <div>
          <label className="block text-text-light dark:text-text-dark font-medium mb-1">
            Cycle Name
          </label>
          <input
            type="text"
            value={cycleName}
            onChange={(e) => setCycleName(e.target.value)}
            className="input w-full"
            placeholder="e.g., Personal Growth Journey"
            disabled={loading}
            aria-label="Cycle name"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input w-full"
              disabled={loading}
              aria-label="Cycle start date"
            />
          </div>
          <div className="flex-1">
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input w-full"
              disabled={loading}
              aria-label="Cycle end date"
            />
          </div>
        </div>
        <button
          type="submit"
          className="btn-primary w-full sm:w-auto"
          disabled={loading}
          aria-label="Add new cycle"
        >
          Add Cycle
        </button>
      </form>
      {cycles.length === 0 && !loading ? (
        <p className="text-neutral text-center">No cycles yet. Create one to start journaling!</p>
      ) : (
        <ul className="space-y-4">
          {cycles.map((cycle) => (
            <li
              key={cycle.id}
              className={`p-4 rounded-md border border-gray-200 dark:border-gray-600 flex justify-between items-center ${selectedCycle?.id === cycle.id ? "bg-secondary text-white" : "bg-background-light dark:bg-background-dark"}`}
            >
              <div
                onClick={() => {
                  setSelectedCycle(cycle);
                  setTab("journal");
                }}
                className="cursor-pointer flex-1"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSelectedCycle(cycle);
                    setTab("journal");
                  }
                }}
                aria-label={`Select cycle ${cycle.name}`}
              >
                <h3 className="text-lg font-medium">{cycle.name}</h3>
                <p className="text-sm text-neutral">
                  {formatDate(cycle.startDate)} to {formatDate(cycle.endDate)}
                </p>
              </div>
              <button
                onClick={() => deleteCycle(cycle.id)}
                className="text-error hover:underline"
                disabled={loading}
                aria-label={`Delete cycle ${cycle.name}`}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Cycles;